import { apiClient, handleApiRequest } from './client'
import type { ExtractedTimetableEntry } from './timetableStructures'
import type { PdfPageResponse } from '@/types/tokenResolution'

export interface TemplateInfo {
  id: string
  school_id: string
  name: string | null
  tool_name: string | null
  fingerprint: Record<string, unknown> | null
  extraction_config?: Record<string, unknown>
  is_active: boolean
  status: string
  version: number
  created_at: string
  sample_structure_id: string | null
}

export interface QuestionState {
  id: string
  label_de: string
  type: string
  auto_detect: boolean
  required: boolean
  placeholder: string
  options?: string[]
}

export interface SessionState {
  session_id: string
  template_id: string | null
  status: string
  current_group: string
  questions_in_group: number
  current_question_index: number
  current_question: QuestionState | null
  all_groups: Array<{ id: string; label: string; count: number }>
  manual_answers: Record<string, AnswerData>
  auto_answers: Record<string, AnswerData>
  answers: Record<string, AnswerData>
  manual_answers_count: number
  total_manual_questions: number
}

export interface AnswerData {
  question_id?: string
  value?: string
  extraction_type?: string
  sub_fields?: Array<{ key: string; value: string }>
  auto_filled_from?: string
  skipped?: boolean
  click_coords?: { x: number; y: number; page?: number }
}

export interface TemplateResult {
  template_id: string
  entries_count: number
  structure_id: string | null
  status: string
}

export interface ApplyResult {
  entries_count: number
  structure_id: string | null
}

export interface JobStatusResponse {
  id: string
  status: string
  progress: number
  message: string | null
  result: {
    structure_id: string | null
    entries_count: number
    processing_time_seconds: number
  } | null
  error: string | null
  created_at: string
  completed_at: string | null
}

export interface JobCreateResponse {
  job_id: string
  status: string
  message: string
}

export interface AutoDetectResult {
  value: string
  extraction_type: string
  sub_fields?: Array<{ key: string; value: string }>
  error?: string
}

export const templateTrainingApi = {
  startTraining: (uploaded_file_id: string) =>
    handleApiRequest<SessionState>(apiClient.post('/api/v1/templates/start', null, {
      params: { uploaded_file_id },
    })),

  getSession: (sessionId: string) =>
    handleApiRequest<SessionState>(apiClient.get(`/api/v1/templates/${sessionId}`)),

  answer: (sessionId: string, data: { 
    question_id: string; 
    value?: string; 
    extraction_type?: string; 
    cell_info?: Record<string, unknown>; 
    click_coords?: { x: number; y: number; page?: number };
    sub_fields?: Array<{ key: string; value: string }>;
    pause_start?: string;
    pause_end?: string;
  }) =>
    handleApiRequest<SessionState>(apiClient.post(`/api/v1/templates/${sessionId}/answer`, data)),

  autoDetect: async (sessionId: string, questionId: string, clickCoords?: { x: number; y: number; page?: number }) => {
    const response = await apiClient.post<AutoDetectResult>(`/api/v1/templates/${sessionId}/auto-detect`, {
      question_id: questionId,
      click_coords: clickCoords,
    }, {
      timeout: 60000,
    })
    return response.data
  },

  skip: (sessionId: string) =>
    handleApiRequest<SessionState>(apiClient.post(`/api/v1/templates/${sessionId}/skip`)),

  back: (sessionId: string) =>
    handleApiRequest<SessionState>(apiClient.post(`/api/v1/templates/${sessionId}/back`)),

  complete: (sessionId: string, data: { name?: string; auto_apply_to_existing?: boolean }) =>
    handleApiRequest<TemplateResult>(apiClient.post(`/api/v1/templates/${sessionId}/complete`, data, {
      timeout: 300000, // 5 minutes — Vision API extraction + structure creation can take 3+ minutes
    })),

  getPagePreview: (sessionId: string, pageNum: number) =>
    `/api/v1/templates/${sessionId}/page/${pageNum}/preview`,

  listTemplates: () =>
    handleApiRequest<TemplateInfo[]>(apiClient.get('/api/v1/templates')),

  applyTemplate: (templateId: string, uploaded_file_id: string) =>
    handleApiRequest<ApplyResult>(apiClient.post(`/api/v1/templates/${templateId}/apply`, { uploaded_file_id })),

  applyTemplateAsync: (templateId: string, uploaded_file_id: string) =>
    handleApiRequest<JobCreateResponse>(apiClient.post(`/api/v1/templates/${templateId}/apply-async`, { uploaded_file_id })),

  getJobStatus: (jobId: string) =>
    handleApiRequest<JobStatusResponse>(apiClient.get(`/api/v1/templates/jobs/${jobId}/status`)),

  cancelJob: (jobId: string) =>
    handleApiRequest<{ message: string }>(apiClient.post(`/api/v1/templates/jobs/${jobId}/cancel`)),

  deleteTemplate: (templateId: string) =>
    handleApiRequest<{ message: string; id: string }>(apiClient.delete(`/api/v1/templates/${templateId}`)),

  deleteAllTemplates: () =>
    handleApiRequest<{ message: string; count: number }>(apiClient.delete('/api/v1/templates/all')),

  updateTemplate: (templateId: string, data: { name?: string; is_active?: boolean; extraction_config?: Record<string, unknown> }) =>
    handleApiRequest<TemplateInfo>(apiClient.patch(`/api/v1/templates/${templateId}`, data)),

  getTemplateConfig: (templateId: string) =>
    handleApiRequest<{ id: string; name: string | null; tool_name: string | null; extraction_config: Record<string, unknown>; is_active: boolean; status: string; version: number; created_at: string }>(apiClient.get(`/api/v1/templates/${templateId}/config`)),

  getTemplatePdfPage: (templateId: string, pageNumber?: number, opts?: { token?: string; sessionId?: string }) =>
    handleApiRequest<PdfPageResponse>(apiClient.get(`/api/v1/templates/${templateId}/pdf-page`, {
      params: {
        ...(pageNumber ? { page_number: pageNumber } : {}),
        ...(opts?.token ? { token: opts.token } : {}),
        ...(opts?.sessionId ? { source_session_id: opts.sessionId } : {}),
      },
    })),

  approveTemplate: (templateId: string) =>
    handleApiRequest<{ message: string; template_id: string; status: string }>(apiClient.post(`/api/v1/templates/${templateId}/approve`)),

  rejectTemplate: (templateId: string, reason?: string) =>
    handleApiRequest<{ message: string; template_id: string; status: string }>(apiClient.post(`/api/v1/templates/${templateId}/reject`, { reason })),

  updateReviewEntry: (entryId: string, data: Record<string, string>) =>
    handleApiRequest<{ message: string; entry_id: string }>(apiClient.patch(`/api/v1/templates/review/${entryId}`, data)),

  aiCorrectEntry: (entryId: string, fieldName: string, feedback: string) =>
    handleApiRequest<{ message: string; field_name: string; current_value: string; corrected_value: string }>(
      apiClient.post(`/api/v1/templates/review/${entryId}/ai-correct`, { field_name: fieldName, feedback }),
    ),

  getEnhancedEntries: (structureId: string, include_multi_period: boolean = true) =>
    handleApiRequest<{ entries: ExtractedTimetableEntry[]; multi_period_detected: boolean }>(
      apiClient.get(`/api/v1/templates/enhanced-entries/${structureId}`, {
        params: { include_multi_period }
      })
    ),
}

export default templateTrainingApi